import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import _ from "lodash";
import {
  List,
  Text,
  Modal,
  Flex,
  Box,
  Dropdown,
  Button,
  Avatar,
  Heading,
  Input
} from "samba-ui";
import styled from "styled-components";
import { loadAdminUsers } from "../redux/users";
import NotifyUsersEmailApproval from "./NotifyUsersEmailApproval";

const StyledButton = styled(Button)`
  float: right;
`;

const StyledInput = styled(Input)`
  width: 300px;
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
`;

const searchOptions = [
  { label: "name", value: "name" },
  { label: "family name", value: "family_name" },
  { label: "email", value: "email" },
  { label: "email domain", value: "email.domain" }
];

const AddNewGroupPermissionModal = ({
  users,
  isOpen,
  onClose,
  onUpdate,
  onSubmit,
  selectedPermission,
  loadAdminUsers
}) => {
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [selectedUsers, setSelectedUsers] = useState({});
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState({
    label: "name",
    value: "name"
  });
  const [isInternal, setIsInternal] = useState(false);

  const [isNotifyModalOpen, setIsNotifyModalOpen] = useState(false);

  useEffect(() => {
    if (selectedPermission) {
      setName(selectedPermission.name);
      setSelectedUsers(
        selectedPermission.users.reduce((acc, user) => {
          acc[user.userId] = user;
          return acc;
        }, {})
      );
      setIsInternal(selectedPermission.isInternal);
    }
  }, [selectedPermission]);

  const debounce = useCallback(
    _.debounce(async () => {
      if (!searchValue) {
        return;
      }
      setLoading(true);
      const query = `${selectedOption.value}:${searchValue}*`;
      await loadAdminUsers(0, query);
      setLoading(false);
    }, 500),
    [searchValue, selectedOption]
  );

  useEffect(() => {
    debounce();

    return debounce.cancel;
  }, [debounce]);

  const clearState = () => {
    setName("");
    setSelectedUsers([]);
    setIsInternal(false);
  };

  const onCreateClick = () => {
    const body = { name, users: Object.keys(selectedUsers), isInternal };
    if (selectedPermission) {
      setIsNotifyModalOpen(true);
    } else {
      onSubmit(body);
      clearState();
    }
  };

  const onClickSendEmail = ({ notifyUser, notifyFinance }) => {
    const body = {
      name,
      users: Object.keys(selectedUsers),
      isInternal,
      notifyUser,
      notifyFinance
    };
    onUpdate(selectedPermission.id, body);
    clearState();
  };

  const isDisabled = !name || !Object.keys(selectedUsers).length;

  const onUserSelect = user => {
    if (selectedUsers[user.userId]) {
      setError(`${user.name} is already selected`);
      return;
    }

    setSelectedUsers({ ...selectedUsers, [user.userId]: user });
    setSearchValue("");
    setError("");
  };

  const onClearClick = id => {
    delete selectedUsers[id];
    setSelectedUsers({ ...selectedUsers });
  };

  return (
    <Modal
      width={800}
      isOpen={isOpen}
      onRequestClose={() => {
        onClose();
        clearState();
      }}
    >
      <Modal.Header borderColor="#d6d6d6 !important">
        <Heading fontSize={2} fontWeight={500} color="secondary">
          Add new group permission
        </Heading>
      </Modal.Header>
      <Modal.Content>
        <Box mb={4}>
          <Flex alignItems="center" justifyContent="space-between">
            <Text>Group Permission Name:</Text>
            <StyledInput
              placeholder="Group permission name"
              onChange={e => setName(e.target.value)}
              value={name}
            />
          </Flex>
          <Flex mt={3} alignItems="center" justifyContent="space-between">
            <Text>Internal permission:</Text>
            <Checkbox
              type="checkbox"
              checked={isInternal}
              onChange={e => setIsInternal(e.target.checked)}
            />
          </Flex>
        </Box>
        <Box height={500} style={{ overflow: "scroll" }}>
          <Flex mb={2} alignItems="center">
            <Text>Search Samba Users by</Text>
            <Box width={140} ml={2}>
              <Dropdown
                options={searchOptions}
                selected={selectedOption}
                onSelect={setSelectedOption}
              />
            </Box>
          </Flex>
          {error && (
            <Box mt={2}>
              <Text color="alert">{error}</Text>
            </Box>
          )}
          <Box style={{ position: "relative" }}>
            <Input
              style={{ width: "100%" }}
              value={searchValue}
              onChange={e => setSearchValue(e.target.value)}
            />
            <Box
              style={{
                maxHeight: 350,
                overflow: "auto",
                position: "absolute",
                top: 40,
                width: "100%"
              }}
            >
              {loading ? (
                <List.Loading style={{ background: "#fff" }} />
              ) : searchValue && Object.keys(users).length ? (
                Object.keys(users).map(id => {
                  const user = users[id];
                  return (
                    <List.Item
                      key={user.userId}
                      onClick={() => onUserSelect(user)}
                      style={{
                        width: "100%",
                        background: "#fff",
                        borderColor: "#ececec"
                      }}
                    >
                      <SingleUser user={user} />
                    </List.Item>
                  );
                })
              ) : searchValue && !Object.keys(users).length ? (
                <List.Empty>No Result Found!</List.Empty>
              ) : (
                <></>
              )}
            </Box>
          </Box>
          <Box my={3}>
            <Text fontSize={2} fontWeight={500}>
              Selected Users
            </Text>
          </Box>
          {Object.keys(selectedUsers).map(id => {
            const user = selectedUsers[id];
            return (
              <SingleUser
                clearable
                onClear={() => onClearClick(id)}
                user={user}
                style={{ borderBottom: "1px solid #ececec" }}
                mt={3}
                pb={3}
              />
            );
          })}
        </Box>
      </Modal.Content>
      <Modal.Actions justifyContent="flex-end" borderColor="#d6d6d6 !important">
        <Flex>
          <Box mr={3}>
            <Button
              variant="secondary"
              onClick={() => {
                onClose();
                clearState();
              }}
            >
              Cancel
            </Button>
          </Box>
          <StyledButton
            variant="primary"
            onClick={onCreateClick}
            disabled={isDisabled}
          >
            {selectedPermission ? "Update" : "Create"}
          </StyledButton>
        </Flex>
      </Modal.Actions>
      <NotifyUsersEmailApproval
        isOpen={isNotifyModalOpen}
        onClose={() => setIsNotifyModalOpen(false)}
        onSubmit={onClickSendEmail}
      />
    </Modal>
  );
};

AddNewGroupPermissionModal.defaultProps = {
  users: {}
};

AddNewGroupPermissionModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  onUpdate: PropTypes.func,
  selectedPermission: PropTypes.object,
  loadAdminUsers: PropTypes.func,
  users: PropTypes.object
};

const mapStateToProps = state => ({
  users: state.users
});

const mapDispatchToProps = dispatch => ({
  loadAdminUsers: (page, query) => dispatch(loadAdminUsers(page, query))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AddNewGroupPermissionModal);

const SingleUser = ({ user, style, clearable, onClear, ...props }) => {
  return (
    <Flex alignItems="center" key={user.userId} style={style} {...props}>
      <Avatar
        src={user.imageURL}
        mr={2}
        width={"40px !important"}
        height={"40px !important"}
      />
      <Box>
        <Box>
          <Text fontSize={1} color="palette.ink.normal">
            {user.name}
          </Text>
        </Box>
        <Text fontSize={1} color="palette.ink.normal">
          {user.email}
        </Text>
      </Box>
      <Box style={{ marginLeft: "auto" }}>
        {clearable ? (
          <Button variant="destructive" onClick={onClear}>
            X
          </Button>
        ) : (
          <Button>+</Button>
        )}
      </Box>
    </Flex>
  );
};

SingleUser.propTypes = { user: PropTypes.object };
